import { useState, useEffect } from "react";
import { Routes, Route, useNavigate, useLocation } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import { adminGetNotifications, adminMarkNotificationsRead } from "@/api";
import AdminUsers from "./AdminUsers";
import AdminApplications from "./AdminApplications";
import AdminDisbursements from "./AdminDisbursements";
import AdminAgents from "./AdminAgents";
import AdminReports from "./AdminReports";
import AdminOverview from "./AdminOverview";
import AdminContractsMandates from "./AdminContractsMandates";
import AdminCommissions from "./AdminCommissions";
import AdminOffers from "./AdminOffers";
import AdminSettings from "./AdminSettings";
import AdminAgentApplications from "./AdminAgentApplications";
import AdminUserDetail from "./AdminUserDetail";
import AdminAuditLogs from "./AdminAuditLogs";
import AdminAadhaarQueue from "./AdminAadhaarQueue";
import AdminEmployees from "./AdminEmployees";
import AdminAppRelease from "./AdminAppRelease";
import { LayoutDashboard, Users, FileText, Banknote, UserCog, BarChart3, LogOut, Menu, X, Shield, DollarSign, Gift, Settings, UserPlus, Bell, ScrollText, ShieldCheck, UserCheck, Smartphone } from "lucide-react";

const NAV_ITEMS = [
  { path: "/admin/dashboard", label: "Overview", icon: LayoutDashboard },
  { path: "/admin/users", label: "Users", icon: Users },
  { path: "/admin/aadhaar-queue", label: "Aadhaar Review", icon: ShieldCheck },
  { path: "/admin/applications", label: "Applications", icon: FileText },
  { path: "/admin/contracts", label: "Contracts", icon: Shield },
  { path: "/admin/disbursements", label: "Disbursements", icon: Banknote },
  { path: "/admin/agents", label: "Agents", icon: UserCog },
  { path: "/admin/employees", label: "Employees", icon: UserCheck },
  { path: "/admin/agent-applications", label: "Agent Apps", icon: UserPlus },
  { path: "/admin/commissions", label: "Commissions", icon: DollarSign },
  { path: "/admin/offers", label: "Offers", icon: Gift },
  { path: "/admin/reports", label: "Reports", icon: BarChart3 },
  { path: "/admin/audit-logs", label: "Audit Logs", icon: ScrollText },
  { path: "/admin/app-release", label: "App Release", icon: Smartphone },
  { path: "/admin/settings", label: "Settings", icon: Settings },
];

export default function AdminDashboard() {
  const navigate = useNavigate();
  const location = useLocation();
  const { logout } = useAuth();
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const [unreadCount, setUnreadCount] = useState(0);

  useEffect(() => {
    loadNotifications();
  }, []);

  const loadNotifications = async () => {
    try {
      const res = await adminGetNotifications();
      setUnreadCount(res.data.unread_count || 0);
    } catch (err) {
      console.error("Failed to load notifications:", err);
    }
  };

  const markNotificationsRead = async () => {
    try {
      await adminMarkNotificationsRead();
      setUnreadCount(0);
    } catch (err) {
      console.error("Failed to mark notifications as read:", err);
    }
  };

  const handleLogout = () => {
    logout();
    navigate("/admin/login");
  };

  return (
    <div className="min-h-screen bg-[#FAFAFA] flex">
      {/* Modern Glassmorphism Sidebar */}
      <div className={`fixed inset-y-0 left-0 z-50 w-64 backdrop-blur-xl bg-white/90 border-r border-gray-200/50 transform transition-transform duration-200 ease-in-out lg:relative lg:translate-x-0 ${
        sidebarOpen ? "translate-x-0" : "-translate-x-full"
      }`}>
        <div className="flex flex-col h-full">
          <div className="flex items-center justify-between p-6 border-b border-gray-200/50">
            <div className="flex items-center gap-2">
              <img src="/assets/logo-icon-blue.png" alt="Rebuilt" className="h-8 w-8" />
              <div>
                <h1 className="text-lg font-black text-[#0A2540] tracking-tight" style={{fontFamily:'Cabinet Grotesk, Outfit, sans-serif'}}>REBUILT</h1>
                <p className="text-[8px] uppercase tracking-[0.25em] text-[#1565C0] font-bold">Admin</p>
              </div>
            </div>
            <button onClick={() => setSidebarOpen(false)} className="lg:hidden text-[#4B5563] hover:text-[#0A2540]">
              <X className="h-6 w-6" />
            </button>
          </div>

          <nav className="flex-1 p-4 space-y-2 overflow-y-auto">
            {NAV_ITEMS.map(({ path, label, icon: Icon }) => (
              <button
                key={path}
                onClick={() => navigate(path)}
                className={`flex items-center gap-3 px-5 py-3 rounded-xl transition-all w-full text-left ${
                  location.pathname === path
                    ? "bg-[#1565C0] text-white shadow-sm"
                    : "text-[#4B5563] hover:bg-gray-100 hover:text-[#0A2540]"
                }`}
              >
                <Icon className="h-5 w-5" strokeWidth={1.5} />
                <span className="font-semibold text-sm">{label}</span>
              </button>
            ))}
          </nav>

          <div className="p-4 border-t border-gray-200/50">
            <button
              onClick={markNotificationsRead}
              className="flex items-center gap-3 px-5 py-3 rounded-xl w-full text-left text-gray-600 hover:bg-gray-100 transition-all mb-2 relative"
            >
              <Bell className="h-5 w-5" strokeWidth={1.5} />
              <span className="font-semibold text-sm">Notifications</span>
              {unreadCount > 0 && (
                <span className="ml-auto w-5 h-5 bg-red-500 text-white text-[10px] font-bold rounded-full flex items-center justify-center">
                  {unreadCount}
                </span>
              )}
            </button>
            <button
              onClick={handleLogout}
              className="flex items-center gap-3 px-5 py-3 rounded-xl w-full text-left text-[#EF4444] hover:bg-[#FEF2F2] transition-all"
            >
              <LogOut className="h-5 w-5" strokeWidth={1.5} />
              <span className="font-semibold text-sm">Logout</span>
            </button>
          </div>
        </div>
      </div>

      {/* Overlay */}
      {sidebarOpen && (
        <div
          className="fixed inset-0 bg-black/50 z-40 lg:hidden"
          onClick={() => setSidebarOpen(false)}
        />
      )}

      {/* Main Content */}
      <div className="flex-1 flex flex-col min-w-0">
        <div className="bg-white/80 backdrop-blur-xl border-b border-gray-200/50 px-6 py-4 lg:hidden flex items-center justify-between">
          <button onClick={() => setSidebarOpen(true)} className="text-[#0A2540]">
            <Menu className="h-6 w-6" />
          </button>
          <button onClick={markNotificationsRead} className="relative p-2 text-gray-500 hover:text-gray-800">
            <Bell className="h-5 w-5" />
            {unreadCount > 0 && (
              <span className="absolute -top-0.5 -right-0.5 w-4 h-4 bg-red-500 text-white text-[9px] font-bold rounded-full flex items-center justify-center">
                {unreadCount}
              </span>
            )}
          </button>
        </div>

        <div className="p-6 sm:p-8">
          <Routes>
            <Route path="dashboard" element={<AdminOverview />} />
            <Route path="users" element={<AdminUsers />} />
            <Route path="aadhaar-queue" element={<AdminAadhaarQueue />} />
            <Route path="applications" element={<AdminApplications />} />
            <Route path="contracts" element={<AdminContractsMandates />} />
            <Route path="disbursements" element={<AdminDisbursements />} />
            <Route path="agents" element={<AdminAgents />} />
            <Route path="employees" element={<AdminEmployees />} />
            <Route path="commissions" element={<AdminCommissions />} />
            <Route path="offers" element={<AdminOffers />} />
            <Route path="reports" element={<AdminReports />} />
            <Route path="settings" element={<AdminSettings />} />
            <Route path="audit-logs" element={<AdminAuditLogs />} />
            <Route path="app-release" element={<AdminAppRelease />} />
            <Route path="agent-applications" element={<AdminAgentApplications />} />
            <Route path="user/:userId" element={<AdminUserDetail />} />
            <Route path="*" element={<AdminOverview />} />
          </Routes>
        </div>
      </div>
    </div>
  );
}
